/**
 * Claude Desktop observability (local) — session-scoped MITM tap.
 *
 * Wraps Anthropic's Claude Desktop in a local mitmproxy that taps its
 * `chat_conversations` traffic and POSTs each turn to the local container's
 * `/api/local/capture`, which stores it in PGLite so the conversations show up
 * in the dashboard Events page. Store-only — no blocking/grading (deferred).
 *
 * This is the LOCAL counterpart of the cloud tap embedded in
 * packages/api/src/routes/proxyTokens.ts. It captures the full per-turn
 * trajectory — user + assistant text, tool calls, tool results (incl. web
 * search), and thinking. (It omits only the cloud version's account-identity and
 * file-upload metadata, which local single-user observability doesn't need.)
 * KEEP THE PARSING IN SYNC with proxyTokens.ts when Claude Desktop's internal
 * API changes — that's the single biggest drift risk.
 *
 * macOS only (keychain trust + Claude.app + osascript). Invoked from
 * `synkro install` behind an explicit consent prompt.
 *
 * Security: capture URL + auth token are passed via ENV VARS (never interpolated
 * into the addon source) so config can't become executable Python (CWE-94). The
 * addon is fully STATELESS — each capture is built from one conversation GET
 * response, with no globals/class attributes shared across flows (CWE-362,
 * CWE-200). For local single-user use the captured turn carries no extracted
 * account identity; the server attributes it to the local identity.
 */
import { spawn } from 'node:child_process';
import { writeFileSync, mkdtempSync, mkdirSync, readFileSync, existsSync } from 'node:fs';
import { join } from 'node:path';
import { homedir } from 'node:os';
import { installClaudeDesktopMcpConfig } from './mcpConfig.js';

// The container's local REST port (matches LOCAL_API_BASE in the dashboard's
// local-fetch.ts). The capture route lives behind the SERVER_TOKEN Bearer gate.
const CAPTURE_URL = 'http://127.0.0.1:18931/api/local/capture';
const SCAN_URL = 'http://127.0.0.1:18931/api/local/scan-upload';
const SCAN_TURN_URL = 'http://127.0.0.1:18931/api/local/scan-turn';
const DLP_POLICY_URL = 'http://127.0.0.1:18931/api/local/dlp-policy';
const TURN_VERDICTS_URL = 'http://127.0.0.1:18931/api/local/turn-verdicts';
const TURN_VERDICT_URL = 'http://127.0.0.1:18931/api/local/turn-verdict';
const MCP_EVENT_URL = 'http://127.0.0.1:18931/api/local/mcp/event';
const JWT_PATH = join(homedir(), '.synkro', '.mcp-jwt');

/** Is Claude Desktop installed? (cheap check for the common install path) */
export function claudeDesktopInstalled(): boolean {
  return process.platform === 'darwin'
    && existsSync('/Applications/Claude.app/Contents/MacOS/Claude');
}

// The mitmproxy addon — STATIC, STATELESS Python source. Config (capture URL +
// token) is read from the environment at runtime, NOT interpolated in (CWE-94).
// No globals or class attributes persist across flows: each capture is built
// entirely from one conversation GET response (CWE-362, CWE-200).
const ADDON_PY = `import os, json, threading, urllib.request, urllib.error, re, base64, time, hashlib
from mitmproxy import http

def _safe_url(u):
    # CWE-918: these URLs come from the environment the installer sets — accept ONLY
    # the loopback container endpoints, never an arbitrary host from a tampered env.
    return u if (u.startswith("http://127.0.0.1:") or u.startswith("http://localhost:")) else ""

CAPTURE_URL = _safe_url(os.environ.get("SYNKRO_CAPTURE_URL", ""))
TOKEN = os.environ.get("SYNKRO_TAP_TOKEN", "")
SCAN_URL = _safe_url(os.environ.get("SYNKRO_SCAN_URL", ""))
SCAN_TURN_URL = _safe_url(os.environ.get("SYNKRO_SCAN_TURN_URL", ""))
POLICY_URL = _safe_url(os.environ.get("SYNKRO_DLP_POLICY_URL", ""))
TURN_VERDICTS_URL = _safe_url(os.environ.get("SYNKRO_TURN_VERDICTS_URL", ""))
TURN_VERDICT_URL = _safe_url(os.environ.get("SYNKRO_TURN_VERDICT_URL", ""))
MCP_EVENT_URL = _safe_url(os.environ.get("SYNKRO_MCP_EVENT_URL", ""))
# History backfill is OPT-IN: only when the user runs synkro claude-desktop with
# --backfill. Otherwise importing past conversations on every tap launch is wasted
# work (and re-floods the feed). When set, it also bypasses the 12h throttle marker.
BACKFILL_ENABLED = os.environ.get("SYNKRO_CD_BACKFILL", "") == "1"
# Token file path — the SERVER_TOKEN rotates whenever the container is recreated,
# so we re-read it from disk on a 401 instead of needing a tap restart.
TOKEN_FILE = os.environ.get("SYNKRO_TAP_TOKEN_FILE", "")
_token_lock = threading.Lock()

def _reload_token():
    global TOKEN
    if not TOKEN_FILE: return
    try:
        with open(TOKEN_FILE) as f:
            t = f.read().strip()
        if t:
            with _token_lock: TOKEN = t
            print("[synkro] auth token reloaded after 401 (container likely recreated)")
    except Exception:
        pass

def _post_json(url, payload_bytes, timeout):
    # POST JSON with auto-reauth: on a 401 (token rotated by a container recreate),
    # re-read the token file and retry ONCE. CWE-362: TOKEN is always snapshotted
    # under the lock before use, never read bare.
    for attempt in range(2):
        with _token_lock: tok = TOKEN
        try:
            req = urllib.request.Request(url, data=payload_bytes, headers={"Content-Type": "application/json", "Authorization": "Bearer " + tok})
            with urllib.request.urlopen(req, timeout=timeout) as resp:
                return resp.read()
        except urllib.error.HTTPError as e:
            if e.code == 401 and attempt == 0:
                _reload_token(); continue
            raise
    return None

# Governance signals parsed from the completion SSE (usage window + web-search
# sources), keyed by conversation id. The completion POST always precedes the
# tree GET for the same turn, so the tree capture below reads + clears this.
# Guarded by a lock — tap callbacks can run concurrently across flows (CWE-362).
_sse_meta = {}
_sse_lock = threading.Lock()
# Session-global MCP server registry (uuid -> {name,url,connected,tools}), parsed
# from the mcp/v2/bootstrap SSE. All access guarded by _mcp_lock (CWE-362).
_mcp_registry = {}
_mcp_lock = threading.Lock()

def _fmt_tool_name(raw):
    # Claude Desktop names connector tool_use blocks as "<ServerName><tool>" with
    # no delimiter (e.g. "Linearlist_issues"). Split it back to "Server:tool" using
    # the captured MCP registry so the dashboard can resolve the server favicon +
    # render a clean label. Exact server+tool match only — never a fuzzy guess.
    if not raw or ":" in raw: return raw
    try:
        with _mcp_lock:
            servers = list(_mcp_registry.values())
        for s in servers:
            sname = s.get("name", "")
            if not sname or not raw.startswith(sname): continue
            rest = raw[len(sname):]
            for t in s.get("tools", []):
                tn = t.get("name", "")
                if tn and rest == tn:
                    return sname + ":" + tn
    except Exception:
        pass
    return raw

def _map_full_conversation(all_msgs):
    # Full conversation [{role, content, tool_calls?}] + 'tool' result messages, in
    # order, for the session-log tables (the Session Timeline card on the event page).
    # Lighter than the per-turn capture: role + text + tool_calls + tool results.
    out = []
    for msg in all_msgs:
        sender = msg.get("sender", "")
        role = "user" if sender == "human" else "assistant"
        blocks = msg.get("content", [])
        texts = []; tcs = []; results = []
        if isinstance(blocks, list):
            for b in blocks:
                if isinstance(b, str): texts.append(b); continue
                if not isinstance(b, dict): continue
                bt = b.get("type", "")
                if bt == "text":
                    texts.append(b.get("text", ""))
                elif bt == "tool_use" or bt == "server_tool_use":
                    tcs.append({"name": _fmt_tool_name(b.get("name", "")), "id": b.get("id", ""), "arguments": json.dumps(b.get("input", {}))[:2000]})
                elif bt.endswith("_tool_result") or bt == "tool_result":
                    rc = b.get("content", "")
                    if isinstance(rc, list):
                        rc = " | ".join(str(x.get("text") or x.get("content") or "") for x in rc if isinstance(x, dict))
                    if rc and str(rc).strip():
                        results.append({"role": "tool", "content": str(rc)[:8000], "name": _fmt_tool_name(b.get("name", ""))})
        elif isinstance(blocks, str):
            texts.append(blocks)
        content = " ".join(t for t in texts if t).strip()
        if content or tcs:
            e = {"role": role, "content": content}
            if tcs: e["tool_calls"] = tcs
            out.append(e)
        for r in results: out.append(r)
        if len(out) >= 2000: break
    return out

def _emit_mcp_event(server, tool, ev_type, eid, convo_id=""):
    # Post a Claude Desktop connector (MCP) lifecycle/tool_call event to the same
    # mcp_events store the CC/Cursor mcp-gate writes, so Desktop connectors show on
    # the MCP Servers page. eid is deterministic so re-captures dedupe (server
    # ON CONFLICT DO NOTHING). Fire-and-forget; never blocks the proxied request.
    if not (MCP_EVENT_URL and TOKEN and server): return
    try:
        payload = json.dumps({
            "event_id": eid,
            "session_id": (str(convo_id)[:80] or None),
            "server_name": str(server)[:120],
            "event_type": ev_type,
            "tool_name": (str(tool)[:120] if tool else None),
            "decision": ("allowed" if ev_type == "tool_call" else None),
            "repo": "claude-desktop",
        }).encode("utf-8")
        _post_json(MCP_EVENT_URL, payload, 5)
    except Exception:
        pass

# ── History backfill ──────────────────────────────────────────────────────────
# One-time pull of the user's past claude.ai conversations into the local store, so
# the dashboard shows full Desktop history (like CC's transcript import) — not just
# turns tapped live. Auth (session cookie + org uuid) is captured from a live request
# and kept IN MEMORY (never written to disk). Store-only: backfilled turns are not
# graded (backfill=True). Runs at most once per 12h via a timestamp marker.
#
# SSRF (CWE-918): the only host we ever fetch is claude.ai, and org/conversation ids
# must be UUID-shaped — both validated before any URL is built, so no untrusted value
# can redirect the fetch to an arbitrary host/path.
_backfill_lock = threading.Lock()
_backfill_started = False
_BACKFILL_MARKER = os.path.expanduser("~/.synkro/.cd-backfill-done")

def _is_claude_host(h):
    return h == "claude.ai" or (isinstance(h, str) and h.endswith(".claude.ai"))

def _is_uuidish(s):
    return bool(re.match(r"^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$", s or ""))

def _http_get(url, headers, timeout=25):
    req = urllib.request.Request(url, headers=headers, method="GET")
    with urllib.request.urlopen(req, timeout=timeout) as resp:
        return resp.read()

def _post_backfill_convo(convo, cid):
    all_msgs = convo.get("chat_messages", [])
    if not all_msgs: return
    model = convo.get("model") or "claude-unknown"
    if not model.startswith("claude-"): model = "claude-" + model
    session_messages = _map_full_conversation(all_msgs)
    if not session_messages: return
    msgs = []
    for e in session_messages:
        mm = {"role": e.get("role", "user"), "content": e.get("content", "")}
        if e.get("tool_calls"): mm["tool_calls"] = e["tool_calls"]
        msgs.append(mm)
    # Real conversation timestamp (last activity, fallback created) so the server
    # stamps created_at to when the convo HAPPENED — not "now". Without this every
    # backfill run re-stamps all history to the present and floods it to the top of
    # the feed. Must be ISO8601; the server re-validates and only honors it for
    # backfill rows.
    cts = ""
    for k in ("updated_at", "created_at"):
        v = convo.get(k) if isinstance(convo, dict) else None
        if isinstance(v, str) and re.match(r"^\d{4}-\d{2}-\d{2}T", v):
            cts = v; break
    data = {"request": {"model": model, "messages": msgs[-200:]}, "conversation_id": cid,
            "session_messages": session_messages, "turn_id": "cdbf_" + cid, "backfill": True}
    if cts: data["event_ts"] = cts
    _post_json(CAPTURE_URL, json.dumps(data).encode("utf-8"), 8)

def _backfill(host, org, cookie):
    if not (_is_claude_host(host) and _is_uuidish(org)): return  # defence in depth
    base = "https://" + host
    headers = {"Cookie": cookie, "Accept": "application/json", "User-Agent": "synkro-tap"}
    try:
        convos = json.loads(_http_get(base + "/api/organizations/" + org + "/chat_conversations?limit=200", headers, 25))
        if not isinstance(convos, list): return
    except Exception:
        with _backfill_lock:
            globals()["_backfill_started"] = False  # allow a later request to retry
        return
    print("[synkro] backfill: importing up to " + str(min(len(convos), 200)) + " conversations")
    n = 0
    for c in convos[:200]:
        cid = c.get("uuid", "") if isinstance(c, dict) else ""
        if not _is_uuidish(cid): continue
        try:
            convo = json.loads(_http_get(base + "/api/organizations/" + org + "/chat_conversations/" + cid + "?tree=True&rendering_mode=messages&render_all_tools=true", headers, 25))
            _post_backfill_convo(convo, cid)
            n += 1
        except Exception:
            pass
        time.sleep(0.6)
    try: open(_BACKFILL_MARKER, "w").write(str(int(time.time())))
    except Exception: pass
    print("[synkro] backfill done: " + str(n) + " conversations imported")

def _maybe_backfill(host, org, cookie):
    # Opt-in only: skip entirely unless the user launched with --backfill.
    if not BACKFILL_ENABLED: return
    if not (_is_claude_host(host) and _is_uuidish(org) and cookie and CAPTURE_URL and TOKEN): return
    with _backfill_lock:
        global _backfill_started
        if _backfill_started: return
        _backfill_started = True
    threading.Thread(target=_backfill, args=(host, org, cookie), daemon=True).start()

# ── M3 live-hold state ────────────────────────────────────────────────────────
# Whether to HOLD connector turns (buffer the live completion until graded).
# Fetched once from the server: True only when a BLOCK-mode rule exists (+ not
# silent). Audit-only orgs get False → stream + async audit, ZERO added latency.
_dlp_hold = None
_dlp_lock = threading.Lock()
# Shown live in place of a held connector turn while the single grader (the tree
# GET) runs; replaced by the real content (if clean) or the block notice.
REVIEWING_NOTICE = "\\U0001F6E1\\uFE0F  Synkro is reviewing this response for policy compliance\\u2026"

def _should_hold():
    global _dlp_hold
    with _dlp_lock:
        if _dlp_hold is not None:
            return _dlp_hold
    val = False
    if POLICY_URL:
        try:
            with urllib.request.urlopen(urllib.request.Request(POLICY_URL), timeout=4) as resp:
                val = bool(json.loads(resp.read().decode("utf-8", "replace")).get("hold"))
        except Exception:
            val = False
    with _dlp_lock:
        _dlp_hold = val
    return val

def _scan_turn_call(cid, msgs, connector):
    # Synchronous DLP grade. Returns the block reason (str) on BLOCK, else None.
    # Fail-OPEN — a scan error never blocks the turn.
    if not (SCAN_TURN_URL and TOKEN): return None
    try:
        vb = _post_json(SCAN_TURN_URL, json.dumps({"conversation_id": str(cid)[:80], "messages": msgs, "connector": connector}).encode(), 18)
        vd = json.loads((vb or b"{}").decode("utf-8", "replace"))
        if vd.get("block"):
            return str(vd.get("reason", "restricted data"))[:300]
    except Exception as e:
        print("[synkro] turn scan error (fail-open): " + str(e))
    return None

def _scan_sse_line(line, st):
    # Extract governance metadata (usage window + web-search sources/citations)
    # from one completion SSE line. Shared by the streaming + buffered paths.
    if st["usage"] is None:
        um = re.search(r'"5h":\\{"status":"([^"]*)","resets_at":(\\d+),"utilization":([0-9.]+)', line)
        if um:
            st["usage"] = {"window": "5h", "status": um.group(1), "resets_at": int(um.group(2)), "utilization": float(um.group(3))}
    if len(st["sources"]) < 50:
        for sm in re.finditer(r'"webpage_metadata","site_domain":"([^"]*)"(?:,"favicon_url":"[^"]*")?,"site_name":"([^"]*)"', line):
            d = sm.group(1)
            if d and d not in st["seen"] and len(st["sources"]) < 50:
                st["seen"].add(d)
                st["sources"].append({"domain": d, "name": sm.group(2)})
    if "citation_start_delta" in line and line.startswith("data: ") and len(st["citations"]) < 50:
        try:
            cit = json.loads(line[6:]).get("delta", {}).get("citation", {})
            url = cit.get("url", "")
            if url and url not in st["cseen"]:
                st["cseen"].add(url)
                md = cit.get("metadata") or {}
                st["citations"].append({"title": cit.get("title", ""), "url": url, "domain": md.get("site_domain", "")})
        except Exception:
            pass

def _new_scan_state():
    return {"carry": "", "usage": None, "sources": [], "seen": set(), "citations": [], "cseen": set()}

def _extract_sse_text(body):
    # Pull all visible text out of a buffered completion SSE (assistant text +
    # tool_result content) for grading, and detect whether a tool/connector ran.
    parts = []
    tool = ("tool_use" in body) or ("tool_result" in body)
    total = 0
    for line in body.split("\\n"):
        if total > 2000000: break
        if not line.startswith("data: "): continue
        try: obj = json.loads(line[6:])
        except Exception: continue
        d = obj.get("delta")
        if isinstance(d, dict) and isinstance(d.get("text"), str):
            parts.append(d["text"]); total += len(d["text"])
        cb = obj.get("content_block")
        if isinstance(cb, dict):
            if isinstance(cb.get("text"), str): parts.append(cb["text"]); total += len(cb["text"])
            c = cb.get("content")
            if isinstance(c, str): parts.append(c); total += len(c)
            elif isinstance(c, list):
                for x in c:
                    if isinstance(x, dict) and isinstance(x.get("text"), str): parts.append(x["text"]); total += len(x["text"])
    return ("".join(parts)[:2000000], tool)

def _neutralize_body(body, notice):
    # Buffered BLOCK render of a COMPLETE response: replace the answer with the notice
    # (first text delta, rest blanked), blank text/content content_blocks (incl. the
    # retrieved doc), leave tool_use + thinking + every structural event intact (the
    # tree GET scrubs thinking durably). Rewriting a WHOLE buffered body — not the
    # live stream — is what avoids the "couldn't finish" fragility.
    out = []
    injected = [False]
    for line in body.split("\\n"):
        if not line.startswith("data: "):
            out.append(line); continue
        try: obj = json.loads(line[6:])
        except Exception:
            out.append(line); continue
        changed = False
        d = obj.get("delta")
        if isinstance(d, dict) and d.get("type") == "text_delta" and isinstance(d.get("text"), str):
            d["text"] = (notice if not injected[0] else "")
            injected[0] = True; changed = True
        cb = obj.get("content_block")
        if isinstance(cb, dict):
            if isinstance(cb.get("text"), str) and cb["text"]:
                cb["text"] = ""; changed = True
            if cb.get("content"):
                cb["content"] = ""; changed = True
        out.append(("data: " + json.dumps(obj)) if changed else line)
    return "\\n".join(out)

# Per-conversation DLP verdict cached by the held completion so the tree GET reuses
# it (durable scrub) instead of re-grading. Bounded (CWE-400), lock-guarded (CWE-362).
_turn_verdicts = {}
_tv_lock = threading.Lock()

def _cache_turn_verdict(cid, verdict):
    if not cid: return
    with _tv_lock:
        if len(_turn_verdicts) > 200: _turn_verdicts.clear()
        _turn_verdicts[cid] = verdict

def _pop_turn_verdict(cid):
    with _tv_lock:
        return _turn_verdicts.pop(cid, None)

# ── Per-turn durable verdict helpers (PGLite-backed via local endpoints) ──────
def _msg_blocks(msg):
    b = msg.get("content", [])
    return b if isinstance(b, list) else []

def _msg_key(msg):
    # Stable per-turn id. Prefer the message uuid; fall back to a content hash so the
    # verdict still keys consistently across reopens even if uuid is ever absent.
    u = msg.get("uuid") or msg.get("id")
    if isinstance(u, str) and u:
        return u
    try:
        raw = json.dumps(msg.get("content", ""), sort_keys=True).encode("utf-8", "replace")
        return "h:" + hashlib.sha256(raw).hexdigest()[:40]
    except Exception:
        return ""

def _msg_is_connector(msg):
    # A turn that pulled content via a connector → has a tool_result block (the doc).
    for b in _msg_blocks(msg):
        if isinstance(b, dict) and (b.get("type", "") == "tool_result" or b.get("type", "").endswith("_tool_result")):
            return True
    return False

def _msg_grade_payload(msg):
    # Extract (assistant_text, tool_result_text, connector) from a message's blocks.
    texts = []; tools = []; connector = ""
    for b in _msg_blocks(msg):
        if isinstance(b, str):
            texts.append(b); continue
        if not isinstance(b, dict):
            continue
        bt = b.get("type", "")
        if bt == "text":
            texts.append(b.get("text", "") or "")
        elif bt == "tool_use" or bt == "server_tool_use":
            nm = b.get("name", "") or ""
            if nm and not connector:
                connector = nm.split(":")[0] if ":" in nm else nm
        elif bt == "tool_result" or bt.endswith("_tool_result"):
            rc = b.get("content", "")
            if isinstance(rc, list):
                for x in rc:
                    if isinstance(x, dict):
                        if isinstance(x.get("text"), str): tools.append(x["text"])
                        elif x.get("content"): tools.append(str(x.get("content")))
            elif isinstance(rc, str):
                tools.append(rc)
            ic = b.get("integration_name") or b.get("name") or ""
            if ic and not connector:
                connector = ic
    return ("\\n".join(texts), "\\n".join(tools), connector)

def _get_turn_verdicts(uuids):
    # Batch lookup of durable verdicts from PGLite (one round-trip). {uuid: {blocked,reason}}.
    if not (TURN_VERDICTS_URL and TOKEN and uuids):
        return {}
    try:
        resp = _post_json(TURN_VERDICTS_URL, json.dumps({"uuids": uuids[:500]}).encode("utf-8"), 8)
        if resp:
            o = json.loads(resp.decode("utf-8", "replace"))
            v = o.get("verdicts")
            return v if isinstance(v, dict) else {}
    except Exception as e:
        print("[synkro] verdict lookup error: " + str(e))
    return {}

def _put_turn_verdict(uuid, convo_id, blocked, reason):
    # Persist one graded turn (fire-and-forget, off the tree-GET critical path).
    if not (TURN_VERDICT_URL and TOKEN and uuid):
        return
    def _send():
        try:
            _post_json(TURN_VERDICT_URL, json.dumps({"message_uuid": uuid, "convo_id": convo_id, "blocked": bool(blocked), "reason": reason or ""}).encode("utf-8"), 8)
        except Exception as e:
            print("[synkro] verdict persist error: " + str(e))
    threading.Thread(target=_send, daemon=True).start()

def _intent_before(all_msgs, idx):
    # Most recent human message text before idx — the turn's user intent for grading.
    for j in range(idx - 1, -1, -1):
        if all_msgs[j].get("sender") == "human":
            for b in _msg_blocks(all_msgs[j]):
                if isinstance(b, dict) and b.get("type") == "text": return (b.get("text") or "")[:2000]
                if isinstance(b, str): return b[:2000]
            return ""
    return ""

def _apply_turn_verdicts(all_msgs, convo_id):
    # WHOLE-CONVERSATION per-turn DLP scrub. Mutates all_msgs in place: replaces every
    # BLOCKED assistant turn's content with the notice. Returns True if anything was
    # blocked. Verdicts are durable (PGLite via the local endpoints); each connector
    # turn (a tool_result is present) is graded once on a miss then persisted; pure-chat
    # turns are skipped. Scrubs EVERY blocked turn — not just the latest — so a follow-up
    # can never resurrect an earlier blocked turn. Fail-OPEN.
    asst_idx = [i for i in range(len(all_msgs)) if all_msgs[i].get("sender") != "human"]
    keys = {i: _msg_key(all_msgs[i]) for i in asst_idx}
    ids = [k for k in keys.values() if k]
    verdicts = _get_turn_verdicts(ids) if ids else {}
    do_grade = _should_hold() and SCAN_TURN_URL and TOKEN
    any_blocked = False
    for i in asst_idx:
        key = keys.get(i, "")
        v = verdicts.get(key) if key else None
        if v is None and do_grade and key and _msg_is_connector(all_msgs[i]):
            text, tools, connector = _msg_grade_payload(all_msgs[i])
            if tools.strip() or text.strip():
                scan_msgs = [
                    {"role": "user", "content": _intent_before(all_msgs, i), "name": ""},
                    {"role": "tool", "content": tools[:500000], "name": connector[:200]},
                    {"role": "assistant", "content": text[:8000], "name": ""},
                ]
                reason = _scan_turn_call(convo_id, scan_msgs, connector)
                v = {"blocked": reason is not None, "reason": reason or ""}
                _put_turn_verdict(key, convo_id, v["blocked"], v["reason"])
        if v and v.get("blocked"):
            all_msgs[i]["content"] = [{"type": "text", "text": "[Blocked by Synkro] " + (v.get("reason") or "restricted data")}]
            any_blocked = True
    return any_blocked

def _feed_answer(chunk, st):
    # Accumulate the assistant's ANSWER text (text_delta) as the held stream flows,
    # using a carry for SSE lines split across chunks. The answer reveals the doc's
    # classification, so once enough has arrived we grade it for an early verdict.
    txt = st["carry"] + chunk
    lines = txt.split("\\n")
    st["carry"] = lines[-1][-100000:]  # bound carried partial line (CWE-400)
    for ln in lines[:-1]:
        if not ln.startswith("data: "):
            continue
        try:
            obj = json.loads(ln[6:])
        except Exception:
            continue
        d = obj.get("delta")
        if isinstance(d, dict) and d.get("type") == "text_delta" and isinstance(d.get("text"), str):
            if len(st["answer"]) < 200000:  # bound (CWE-400)
                st["answer"] += d["text"]

def _grade_worker(st, ans):
    # Runs in a BACKGROUND THREAD (off mitmproxy's event loop). The urllib grade
    # releases the GIL during I/O, so the loop keeps flushing keep-alive pings.
    # CWE-362: ans is a snapshot PASSED BY the loop thread (the sole writer of
    # st["answer"]) at thread-creation — the worker never reads shared mutable
    # st["answer"], so there is no data race. It writes only verdict/graded, which the
    # loop reads AFTER graded.is_set() provides the happens-before barrier.
    try:
        ans = ans[:200000]
        reason = _scan_turn_call(st["cid"], [{"role": "assistant", "content": ans, "name": ""}], "") if ans.strip() else None
    except Exception:
        reason = None
    st["verdict"] = {"block": reason is not None, "reason": reason or ""}
    _cache_turn_verdict(st["cid"], st["verdict"])
    st["graded"].set()

def _classify_frame(fr):
    # Inspect one CRLF SSE frame's data line → 'tool_use_start' | 'text_start' | 'other'
    # based on the content_block_start type. Used to find where the FINAL answer begins.
    for ln in fr.split("\\r\\n"):
        if ln.startswith("data: "):
            try:
                o = json.loads(ln[6:])
            except Exception:
                return "other"
            if o.get("type") == "content_block_start":
                cb = o.get("content_block")
                if isinstance(cb, dict):
                    t = cb.get("type")
                    if t == "tool_use":
                        return "tool_use_start"
                    if t == "text":
                        return "text_start"
            return "other"
    return "other"

def _frame_answer_text(fr, st):
    # Accumulate text_delta text from a HELD frame into st["answer"] (for the grade).
    for ln in fr.split("\\r\\n"):
        if ln.startswith("data: "):
            try:
                d = json.loads(ln[6:]).get("delta")
            except Exception:
                continue
            if isinstance(d, dict) and d.get("type") == "text_delta" and isinstance(d.get("text"), str):
                if len(st["answer"]) < 200000:  # CWE-400 bound
                    st["answer"] += d["text"]

def _block_held(frames, notice):
    # Frame-aware, CRLF-PRESERVING block render of the held final-answer frames: replace
    # the answer's text_delta(s) (first→notice, rest→"") and leave every structural frame
    # (content_block_stop / message_delta / message_stop) intact so the message still
    # finalizes cleanly. Operates per-frame on \\r\\n lines → no \\n/\\r\\n mixing, so the
    # framing stays byte-clean (the thing _neutralize_body's \\n split would have broken).
    inj = [False]
    out = []
    for fr in frames:
        nl = []
        for ln in fr.split("\\r\\n"):
            if ln.startswith("data: "):
                try:
                    o = json.loads(ln[6:])
                    d = o.get("delta")
                    if isinstance(d, dict) and d.get("type") == "text_delta" and isinstance(d.get("text"), str):
                        o["delta"]["text"] = notice if not inj[0] else ""
                        inj[0] = True
                        ln = "data: " + json.dumps(o)
                except Exception:
                    pass
            nl.append(ln)
        out.append("\\r\\n".join(nl))
    return ("\\r\\n\\r\\n".join(out) + "\\r\\n\\r\\n") if out else ""

def _make_hold_stream(cid):
    # Pass-through-until-FINAL-ANSWER hold for the claude.ai completion (CRLF SSE; frames
    # split by \\r\\n\\r\\n). A connector turn is: thinking → text(preamble) → tool_use →
    # tool_result → … → text(ANSWER). Only the FINAL answer is in the user's face, so we
    # STREAM every earlier frame VERBATIM (natural thinking/search activity + tool chips,
    # zero mutation) and HOLD only the final answer — the text block that opens AFTER a
    # tool_use — through message_stop. Buffer it (no leak), grade off-thread, heartbeat to
    # keep Desktop alive, then emit the real answer (clean) or a frame-aware block rewrite
    # (restricted). CWE-362: the grade thread gets an immutable answer snapshot; the
    # verdict returns via the Event barrier; the loop is the sole writer of held/answer.
    st = {"cid": cid, "carry": "", "holding": False, "seen_tool": False,
          "held": [], "held_size": 0, "answer": "", "started": False, "meta": _new_scan_state(),
          "graded": threading.Event(), "verdict": {"block": False, "reason": ""}}
    def _hold(data, _st=st):
        if data:
            txt = _st["carry"] + data.decode("utf-8", "replace")
            frames = txt.split("\\r\\n\\r\\n")
            _st["carry"] = frames[-1][-200000:]   # CWE-400 bounded carry (last partial frame)
            out = []
            for fr in frames[:-1]:
                for ln in fr.split("\\r\\n"):
                    _scan_sse_line(ln, _st["meta"])   # governance metadata, all frames
                if not _st["holding"]:
                    k = _classify_frame(fr)
                    if k == "tool_use_start":
                        _st["seen_tool"] = True
                    if k == "text_start" and _st["seen_tool"]:
                        _st["holding"] = True          # the FINAL answer begins here → hold from now
                    else:
                        out.append(fr)                 # thinking / preamble / tool_use / tool_result → verbatim
                        continue
                if _st["held_size"] < 8000000:         # CWE-400 bound before append
                    _st["held"].append(fr); _st["held_size"] += len(fr)
                _frame_answer_text(fr, _st)
            if _st["holding"] and not _st["started"] and len(_st["answer"]) >= 1200:
                _st["started"] = True
                threading.Thread(target=_grade_worker, args=(_st, _st["answer"][:200000]), daemon=True).start()
            if out:
                return ("\\r\\n\\r\\n".join(out) + "\\r\\n\\r\\n").encode("utf-8")  # stream activity verbatim
            return b": synkro\\n\\n"   # answer held → heartbeat (keeps Desktop alive, PROVEN)
        # upstream ended
        m = _st["meta"]
        if (m["usage"] or m["sources"] or m["citations"]) and _st["cid"]:
            with _sse_lock:
                _sse_meta[_st["cid"]] = {"usage": m["usage"], "sources": m["sources"], "citations": m["citations"]}
        if not _st["holding"]:   # no final-answer block detected — everything already streamed
            return _st["carry"].encode("utf-8") if _st["carry"] else b""
        if _st["carry"]:
            _st["held"].append(_st["carry"]); _frame_answer_text(_st["carry"], _st); _st["carry"] = ""
        if not _st["started"] and _st["answer"].strip():  # short answer never hit the 1200 trigger
            _st["started"] = True
            threading.Thread(target=_grade_worker, args=(_st, _st["answer"][:200000]), daemon=True).start()
        held_frames = list(_st["held"])
        def _grade_window(_st2=_st, _frames=held_frames):
            waited = 0.0
            while not _st2["graded"].is_set() and waited < 60.0:
                yield b": synkro\\n\\n"   # heartbeat WHILE grading — keeps Desktop alive (PROVEN)
                time.sleep(0.5)
                waited += 0.5
            v = _st2["verdict"]
            if _st2["graded"].is_set() and v["block"]:
                print("[synkro] HELD+BLOCKED live (reason=" + v["reason"][:60] + ")")
                yield _block_held(_frames, "[Blocked by Synkro] " + v["reason"]).encode("utf-8")
            elif _st2["graded"].is_set():
                yield ("\\r\\n\\r\\n".join(_frames) + "\\r\\n\\r\\n").encode("utf-8")  # clean → real answer
            else:  # grade never landed in 60s — never leak; tree GET backstop finalizes
                print("[synkro] hold: grade timeout → placeholder + tree-GET backstop")
                yield _block_held(_frames, "[Synkro is reviewing this response…]").encode("utf-8")
        return _grade_window()
    return _hold

class SynkroTap:
    def request(self, flow):
        # ── Upload DLP gate ── Scan file uploads BEFORE they reach claude.ai and
        # refuse a blocked one (the file never enters the model). SYNCHRONOUS: this
        # upload waits for the verdict. Fail-OPEN — a scan error never breaks an
        # upload. CWE-200: never print the filename/content (size/rule only).
        r = flow.request
        if not SCAN_URL or not TOKEN: return
        if "claude.ai" not in (r.pretty_host or ""): return
        if r.method != "POST": return
        ct = r.headers.get("content-type", "")
        path = r.path or ""
        is_upload = ("multipart/form-data" in ct) or bool(re.search(r"/(files|upload|convert)(/|$|\\?)", path))
        if not is_upload: return
        # CWE-400: bound everything. Oversized uploads scan on name/type only — we
        # never pull a multi-MB body into memory or forward it to the scanner.
        MAX_RAW = 8 * 1024 * 1024
        try: clen = int(r.headers.get("content-length", "0") or "0")
        except Exception: clen = 0
        if clen and clen > MAX_RAW:
            payload = {"file_name": "", "size": clen, "content_b64": ""}
        else:
            try: body = r.get_content() or b""
            except Exception: return
            if not body or len(body) > MAX_RAW: return
            fname = ""
            fm = re.search(rb'filename="([^"]*)"', body)
            if fm:
                try: fname = fm.group(1).decode("utf-8", "replace")[:300]
                except Exception: fname = ""
            # Extract the uploaded file's RAW bytes from the multipart body so the
            # grader Reads the real file (a PDF/image must be intact, not the
            # multipart envelope). Fall back to the whole body if no part is found.
            file_bytes = None
            bm = re.search(r"boundary=([^;]+)", ct)
            if bm and fm:
                boundary = ("--" + bm.group(1).strip().strip('"')).encode()
                hdr_end = body.find(b"\\r\\n\\r\\n", fm.start())
                if hdr_end != -1:
                    s = hdr_end + 4
                    e = body.find(b"\\r\\n" + boundary, s)
                    if e != -1: file_bytes = body[s:e]
            if file_bytes is None: file_bytes = body
            try: b64 = base64.b64encode(file_bytes).decode("ascii")
            except Exception: return
            payload = {"file_name": fname, "size": len(file_bytes), "content_b64": b64}
        try:
            respb = _post_json(SCAN_URL, json.dumps(payload).encode(), 55)
            verdict = json.loads((respb or b"{}").decode("utf-8", "replace"))
        except Exception as e:
            print("[synkro] upload scan error (fail-open): " + str(e))
            return
        if verdict.get("block"):
            reason = str(verdict.get("reason", "Blocked by Synkro policy"))[:300]
            msg = "Blocked by Synkro: " + reason
            # Return a TERMINAL 400 (invalid_request_error), NOT 403. Claude Desktop's
            # upload client treats 403 as transient/auth and auto-RETRIES it — that's the
            # loop the user sees. A 400 invalid_request_error is non-retryable (the SDK/UI
            # only retry 408/409/429/5xx), so the client stops and surfaces the message.
            # Anthropic's standard error envelope ({"type":"error","error":{...}}) is what
            # the UI knows how to render, so the user sees the reason instead of a spinner.
            flow.response = http.Response.make(
                400,
                json.dumps({"type": "error", "error": {"type": "invalid_request_error", "message": msg}}).encode(),
                {"Content-Type": "application/json"},
            )
            print("[synkro] BLOCKED upload (size=" + str(payload.get("size", 0)) + " rule=" + str(verdict.get("ruleId", "")) + ")")
        else:
            print("[synkro] upload scanned -> allow (size=" + str(payload.get("size", 0)) + ")")

    def responseheaders(self, flow):
        # The answer the user watches TYPE OUT comes from claude.ai's completion — THAT
        # is the stream we HOLD (buffer + heartbeat → no leak, no disconnect; see
        # _make_hold_stream). a-api.anthropic.com is a background call that doesn't carry
        # the answer, so it streams through untouched (default below). The tree GET is
        # buffered for the dashboard + the durable block scrub.
        r = flow.request
        host = r.pretty_host or ""
        if "claude.ai" in host and "chat_conversations" in r.path:
            if r.method == "GET" and "tree" in r.pretty_url:
                return
            if r.method == "POST" and "completion" in r.path:
                cm = re.search(r"chat_conversations/([0-9a-fA-F-]+)", r.path)
                cid = cm.group(1) if cm else ""
                with _mcp_lock:
                    has_servers = len(_mcp_registry) > 0
                # HOLD when a BLOCK-mode rule exists AND connectors are connected — buffer
                # the answer, grade off-thread, heartbeat through the grade, emit the verdict.
                if has_servers and _should_hold():
                    flow.response.stream = _make_hold_stream(cid)
                    return
                # Otherwise just TAP for governance metadata (no hold; audit stays async).
                st = _new_scan_state()
                def _tap(data, _st=st, _cid=cid):
                    if data:
                        try:
                            txt = _st["carry"] + data.decode("utf-8", "replace")
                        except Exception:
                            txt = _st["carry"]
                        lines = txt.split("\\n")
                        _st["carry"] = lines[-1][-200000:]  # bound the carried partial line (CWE-400)
                        for ln in lines[:-1]:
                            _scan_sse_line(ln, _st)
                    else:  # end of stream — flush carry + publish
                        _scan_sse_line(_st["carry"], _st)
                        if (_st["usage"] or _st["sources"] or _st["citations"]) and _cid:
                            with _sse_lock:
                                _sse_meta[_cid] = {"usage": _st["usage"], "sources": _st["sources"], "citations": _st["citations"]}
                    return data
                flow.response.stream = _tap
                return
        if "claude.ai" in (r.pretty_host or "") and "mcp" in r.path and "bootstrap" in r.path:
            return  # buffer the MCP registry bootstrap (parsed in response())
        flow.response.stream = True

    def response(self, flow):
        r = flow.request
        if "claude.ai" not in (r.pretty_host or ""): return
        # MCP server registry from the bootstrap SSE stream.
        if "mcp" in r.path and "bootstrap" in r.path and flow.response:
            try:
                servers = {}
                for line in (flow.response.get_text() or "").split("\\n"):
                    if not line.startswith("data: "): continue
                    try: dd = json.loads(line[6:])
                    except: continue
                    if isinstance(dd.get("servers"), list):
                        for s in dd["servers"]:
                            sid = s.get("uuid", "")
                            if sid: servers[sid] = {"name": s.get("name", ""), "url": s.get("url", ""), "connected": False, "tools": []}
                    if "uuid" in dd and "connected" in dd and dd.get("uuid") in servers:
                        servers[dd["uuid"]]["connected"] = dd.get("connected", False)
                    if "server_uuid" in dd and isinstance(dd.get("tools"), list) and dd.get("server_uuid") in servers:
                        for t in dd["tools"]:
                            servers[dd["server_uuid"]]["tools"].append({"name": t.get("name", ""), "description": (t.get("description") or "")[:200]})
                if servers:
                    with _mcp_lock:
                        _mcp_registry.clear()
                        _mcp_registry.update(servers)
                    print("[synkro] MCP servers: " + str(len(servers)))
                    # Surface each connector on the MCP Servers page (connect/disconnect).
                    for sid, s in servers.items():
                        nm = s.get("name", "")
                        if nm:
                            ev = "connect" if s.get("connected") else "disconnect"
                            _emit_mcp_event(nm, "", ev, "cdmcp_" + ev + "_" + str(sid))
            except Exception:
                pass
            return
        if not ("chat_conversations" in r.path and r.method == "GET" and "tree" in r.pretty_url and flow.response): return
        if not CAPTURE_URL or not TOKEN: return
        try: convo = json.loads(flow.response.get_text())
        except: return
        m = re.search(r"chat_conversations/([0-9a-fA-F-]+)", r.path)
        convo_id = m.group(1) if m else ""
        # One-time history backfill using THIS request's session auth (in-memory only).
        try:
            _om = re.search(r"/organizations/([0-9a-fA-F-]+)/", r.path)
            _maybe_backfill(r.pretty_host, _om.group(1) if _om else "", r.headers.get("cookie", "") or r.headers.get("Cookie", ""))
        except Exception:
            pass
        model = convo.get("model", "unknown")
        settings = convo.get("settings") or {}
        emt = settings.get("enabled_mcp_tools")
        enabled_mcp = list(emt.keys()) if isinstance(emt, dict) else []
        web_search_on = bool(settings.get("enabled_web_search", False))
        platform = convo.get("platform", "")
        all_msgs = convo.get("chat_messages", [])
        if not all_msgs: return
        # Current turn only: last human message and everything after it.
        last_human = -1
        for i in range(len(all_msgs) - 1, -1, -1):
            if all_msgs[i].get("sender") == "human": last_human = i; break
        chat = all_msgs[last_human:] if last_human >= 0 else all_msgs[-2:]
        messages = []
        for msg in chat:
            sender = msg.get("sender", "")
            role = "user" if sender == "human" else "assistant"
            blocks = msg.get("content", [])
            text_parts = []
            tool_calls = []
            tool_results = []
            thinking = ""
            if isinstance(blocks, list):
                for b in blocks:
                    if isinstance(b, str): text_parts.append(b); continue
                    if not isinstance(b, dict): continue
                    bt = b.get("type", "")
                    if bt == "text":
                        text_parts.append(b.get("text", ""))
                    elif bt == "thinking":
                        thinking += b.get("thinking", "") or b.get("text", "")
                    elif bt == "tool_use" or bt == "server_tool_use":
                        _nm = _fmt_tool_name(b.get("name", ""))
                        tool_calls.append({"name": _nm, "id": b.get("id", ""), "arguments": json.dumps(b.get("input", {}))})
                        # A "Server:tool" name means a hosted MCP connector ran → log it
                        # on the MCP Servers page (":" only present for registry matches,
                        # so built-in tools like web_search are excluded).
                        if ":" in _nm:
                            _srv, _, _tl = _nm.partition(":")
                            _emit_mcp_event(_srv, _tl, "tool_call", "cdmcp_tc_" + (b.get("id", "") or hashlib.sha256((convo_id + _nm).encode()).hexdigest()[:24]), convo_id)
                    elif bt.endswith("_tool_result") or bt == "tool_result":
                        rc = b.get("content", "")
                        if isinstance(rc, list):
                            rparts = []
                            for x in rc:
                                if not isinstance(x, dict): continue
                                if x.get("type") == "web_search_result":
                                    sn = x.get("page_content", "") or x.get("snippet", "") or x.get("content", "") or ""
                                    if isinstance(sn, dict): sn = sn.get("text", "")
                                    rparts.append((x.get("title", "") + " (" + x.get("url", "") + ")" + ((" - " + str(sn)[:200]) if sn else "")).strip())
                                elif x.get("text"):
                                    rparts.append(x["text"])
                                elif isinstance(x.get("content"), str):
                                    rparts.append(x["content"])
                            rc = " | ".join(p for p in rparts if p and p.strip())
                        dc = b.get("display_content")
                        if isinstance(dc, dict):
                            rc = rc or dc.get("json_block", "") or dc.get("text", "")
                        # web_search results are server-side (content:[]); we surface the
                        # sources via sse_meta instead. Only record a non-empty result.
                        if rc and str(rc).strip():
                            tool_results.append({"name": _fmt_tool_name(b.get("name", "")), "content": str(rc)})
            elif isinstance(blocks, str):
                text_parts.append(blocks)
            text = " ".join(p for p in text_parts if p).strip()
            if role == "assistant":
                am = {"role": "assistant", "content": text}
                if tool_calls: am["tool_calls"] = tool_calls
                if thinking: am["thinking"] = thinking
                if text or tool_calls or thinking: messages.append(am)
                for tr in tool_results:
                    messages.append({"role": "tool", "content": tr["content"], "name": tr.get("name", "")})
            elif role == "user" and text:
                messages.append({"role": "user", "content": text})
        if not messages: return
        data = {"request": {"model": model, "messages": messages}, "conversation_id": convo_id}
        # Full conversation for the session-log tables (Session Timeline card).
        data["session_messages"] = _map_full_conversation(all_msgs)
        # PER-TURN key: the current turn's assistant message id (same key used for
        # dlp_turn_verdicts). Lets the server store each turn as its OWN event so a
        # blocked turn isn't overwritten/deleted by a later passing turn.
        turn_id = ""
        for _m in reversed(chat):
            if _m.get("sender") != "human":
                turn_id = _msg_key(_m); break
        if turn_id: data["turn_id"] = turn_id
        meta = {}
        if platform: meta["platform"] = platform
        if web_search_on: meta["web_search_enabled"] = True
        if enabled_mcp: meta["enabled_mcp_tools"] = enabled_mcp[:20]
        if meta: data["conversation_metadata"] = meta
        with _sse_lock:
            sm = _sse_meta.pop(convo_id, None)
        if sm: data["sse_meta"] = sm
        with _mcp_lock:
            reg = list(_mcp_registry.values())
        if reg: data["mcp_servers"] = reg
        ntools = 0
        for mm in messages:
            ntools += len(mm.get("tool_calls") or [])
        nsrc = len((sm or {}).get("sources") or [])
        print("[synkro] captured chat (msgs=" + str(len(messages)) + " tools=" + str(ntools) + " sources=" + str(nsrc) + " model=" + model + ")")

        # ── Tier-2 connector DLP gate (WHOLE-CONVERSATION, per-turn, PERSISTENT) ──
        # Scrub every blocked assistant turn (durable PGLite verdicts; connector turns
        # graded once on miss). See _apply_turn_verdicts. Fail-OPEN on any error.
        try:
            any_blocked = _apply_turn_verdicts(all_msgs, convo_id)
        except Exception as e:
            any_blocked = False
            print("[synkro] turn-verdict scrub error (fail-open): " + str(e))

        # Capture the REAL content for audit regardless of the block decision.
        def send(_d=data):
            try:
                _post_json(CAPTURE_URL, json.dumps(_d).encode(), 5)
            except Exception as e:
                print("[synkro] capture error: " + str(e))
        threading.Thread(target=send, daemon=True).start()

        # Re-serialize the tree so the renderer never sees any blocked turn's content.
        if any_blocked:
            try:
                flow.response.text = json.dumps(convo)
                print("[synkro] BLOCKED turn view (whole-convo per-turn, durable)")
            except Exception as e:
                print("[synkro] block scrub failed (fail-open): " + str(e))

addons = [SynkroTap()]
`;

// ── MCP stdio proxy (local) ───────────────────────────────────────────────
// Ported from the cloud tap (packages/api/src/routes/proxyTokens.ts): a stdio
// passthrough that wraps each configured MCP server, taps JSON-RPC tools/call
// requests + responses, and POSTs {tool, args, result, latency} to the local
// capture endpoint with model "mcp:<server>". Bearer auth (vs the cloud's
// x-proxy-token). MCP results are client-side, so this catches what the
// conversation tree may miss. The wrapped command comes from the user's own
// Claude Desktop config (spawning it is the proxy's purpose — CWE-78 exempt);
// the pending map is lock-guarded (CWE-362) and the line buffer bounded (CWE-400).
const MCP_PROXY_PY = `#!/usr/bin/env python3
"""Synkro MCP Proxy (local) — stdio passthrough that taps MCP tool calls."""
import sys, os, json, subprocess, threading, urllib.request, urllib.error, time

CAPTURE_URL = os.environ.get("SYNKRO_CAPTURE_URL", "")
TOKEN = os.environ.get("SYNKRO_TAP_TOKEN", "")
SERVER_NAME = os.environ.get("SYNKRO_MCP_SERVER", "unknown")
# The SERVER_TOKEN rotates on container recreate; re-read it from disk on a 401.
TOKEN_FILE = os.environ.get("SYNKRO_TAP_TOKEN_FILE", "") or os.path.expanduser("~/.synkro/.mcp-jwt")
_token_lock = threading.Lock()

def _reload_token():
    global TOKEN
    try:
        with open(TOKEN_FILE) as f:
            t = f.read().strip()
        if t:
            with _token_lock: TOKEN = t
    except Exception:
        pass

def _post_json(url, payload_bytes, timeout):
    # CWE-362: TOKEN snapshotted under the lock. Auto-reauth retry once on 401.
    for attempt in range(2):
        with _token_lock: tok = TOKEN
        try:
            req = urllib.request.Request(url, data=payload_bytes, headers={"Content-Type": "application/json", "Authorization": "Bearer " + tok})
            with urllib.request.urlopen(req, timeout=timeout) as resp:
                return resp.read()
        except urllib.error.HTTPError as e:
            if e.code == 401 and attempt == 0:
                _reload_token(); continue
            raise
    return None

pending = {}
pending_lock = threading.Lock()

def send_capture(tool_name, tool_args, tool_result, latency_ms):
    if not CAPTURE_URL or not TOKEN: return
    try:
        payload = json.dumps({
            "request": {
                "model": "mcp:" + SERVER_NAME,
                "messages": [
                    {"role": "assistant", "content": "", "tool_calls": [{"name": tool_name, "arguments": json.dumps(tool_args)}]},
                    {"role": "tool", "content": json.dumps(tool_result), "name": tool_name},
                ]
            },
            "mcp": {"server": SERVER_NAME, "tool": tool_name, "latency_ms": latency_ms}
        }).encode()
        _post_json(CAPTURE_URL, payload, 5)
    except Exception as e:
        print("[synkro-mcp] capture error: " + str(e), file=sys.stderr)

def tap_message(data, direction):
    if not isinstance(data, dict): return
    method = data.get("method")
    msg_id = data.get("id")
    if direction == "request" and method == "tools/call":
        params = data.get("params", {})
        tool_name = params.get("name", "unknown")
        tool_args = params.get("arguments", {})
        if msg_id is not None:
            with pending_lock:
                if len(pending) < 1000:
                    pending[msg_id] = {"name": tool_name, "args": tool_args, "start": time.time()}
        print("[synkro-mcp] " + SERVER_NAME + "." + str(tool_name), file=sys.stderr)
    if direction == "response" and msg_id is not None:
        with pending_lock:
            call = pending.pop(msg_id, None)
        if call:
            latency_ms = int((time.time() - call["start"]) * 1000)
            result = data.get("result", data.get("error", {}))
            threading.Thread(target=send_capture, args=(call["name"], call["args"], result, latency_ms), daemon=True).start()

def read_jsonrpc(stream):
    buf = b""
    while True:
        chunk = stream.read(1)
        if not chunk: return None
        if len(buf) < 5000000:  # bound the line buffer (CWE-400)
            buf += chunk
        if chunk == b"\\n":
            line = buf.strip()
            buf = b""
            if not line: continue
            try: return json.loads(line)
            except: continue

def forward_stdin(proc):
    while True:
        msg = read_jsonrpc(sys.stdin.buffer)
        if msg is None: break
        tap_message(msg, "request")
        line = json.dumps(msg) + "\\n"
        try:
            proc.stdin.write(line.encode())
            proc.stdin.flush()
        except: break

def main():
    args = sys.argv[1:]
    if "--" in args:
        sep = args.index("--")
        args = args[sep + 1:]
    if not args:
        print("Usage: mcp_proxy.py -- <command> [args...]", file=sys.stderr)
        sys.exit(1)
    # Spawn the user's own configured MCP server (from claude_desktop_config.json).
    proc = subprocess.Popen(args, stdin=subprocess.PIPE, stdout=subprocess.PIPE, stderr=sys.stderr)
    t = threading.Thread(target=forward_stdin, args=(proc,), daemon=True)
    t.start()
    while True:
        msg = read_jsonrpc(proc.stdout)
        if msg is None: break
        tap_message(msg, "response")
        line = json.dumps(msg) + "\\n"
        try:
            sys.stdout.buffer.write(line.encode())
            sys.stdout.buffer.flush()
        except: break
    proc.wait()
    sys.exit(proc.returncode)

if __name__ == "__main__":
    main()
`;

// Patches Claude Desktop's claude_desktop_config.json to wrap each mcpServers
// entry with the proxy above (command=python3, args=[proxy, --, orig...] + the
// capture env). Idempotent via the _synkro_original marker; the runner backs the
// config up first and restores it on exit.
const MCP_PATCH_PY = `import json, sys
config_path, proxy_path, capture_url, token = sys.argv[1:5]
with open(config_path) as f: config = json.load(f)
servers = config.get("mcpServers", {})
if not servers:
    print("No MCP servers configured")
    sys.exit(1)
patched = 0
for name, server in servers.items():
    if server.get("_synkro_original"): continue
    orig_cmd = server.get("command", "")
    orig_args = server.get("args", [])
    if not orig_cmd: continue
    server["_synkro_original"] = {"command": orig_cmd, "args": orig_args}
    server["command"] = "python3"
    server["args"] = [proxy_path, "--", orig_cmd] + orig_args
    env = server.get("env", {})
    env["SYNKRO_CAPTURE_URL"] = capture_url
    env["SYNKRO_TAP_TOKEN"] = token
    env["SYNKRO_MCP_SERVER"] = name
    server["env"] = env
    patched += 1
with open(config_path, "w") as f: json.dump(config, f, indent=2)
print("Patched " + str(patched) + " MCP servers")
`;

// The bash harness: install mitmproxy if needed, generate + trust a session CA,
// run mitmdump with the addon, launch Claude Desktop through it, and tear it all
// down on exit. Uses SIGTERM (default kill) — never SIGKILL. No secrets here —
// the token reaches the addon via the inherited environment.
function buildRunner(sessionDir: string): string {
  return `#!/bin/bash
set -e
SESSION_DIR=${JSON.stringify(sessionDir)}
PROXY_PORT=8080
MITM_CA_DIR="$SESSION_DIR/ca"
TAP_SCRIPT="$SESSION_DIR/tap.py"
CA_FINGERPRINT=""
MITM_PID=""
SUDO_KEEPALIVE_PID=""
MCP_PROXY="$SESSION_DIR/mcp_proxy.py"
MCP_PATCH="$SESSION_DIR/mcp_patch.py"
CLAUDE_CONFIG="$HOME/Library/Application Support/Claude/claude_desktop_config.json"
CLAUDE_CONFIG_BACKUP="$SESSION_DIR/claude_desktop_config.backup.json"
MCP_PATCHED=false

cleanup() {
  echo ""
  echo "Stopping Claude Desktop observability..."
  [ -n "$MITM_PID" ] && kill "$MITM_PID" 2>/dev/null
  [ -n "$SUDO_KEEPALIVE_PID" ] && kill "$SUDO_KEEPALIVE_PID" 2>/dev/null
  # Restore the original MCP config (unwrap the proxy) on exit.
  if [ "$MCP_PATCHED" = "true" ] && [ -f "$CLAUDE_CONFIG_BACKUP" ]; then
    cp "$CLAUDE_CONFIG_BACKUP" "$CLAUDE_CONFIG" && echo "  MCP config restored"
  fi
  if [ -n "$CA_FINGERPRINT" ]; then
    # Non-interactive (-n) so teardown can NEVER hang on a sudo password prompt
    # (which would swallow the user's Ctrl+C). The keepalive below keeps creds
    # warm so this normally succeeds without a prompt.
    if sudo -n security delete-certificate -Z "$CA_FINGERPRINT" -t /Library/Keychains/System.keychain 2>/dev/null; then
      echo "  Session CA removed from keychain"
    else
      echo "  Could not auto-remove the session CA (sudo timed out)."
      echo "  Remove it manually: sudo security delete-certificate -c mitmproxy /Library/Keychains/System.keychain"
    fi
  fi
  echo "Done."
  exit 0
}
trap cleanup INT TERM EXIT

if ! command -v mitmdump &>/dev/null; then
  echo "Installing mitmproxy..."
  if command -v brew &>/dev/null; then brew install mitmproxy; else echo "mitmproxy required: brew install mitmproxy"; exit 1; fi
fi

mkdir -p "$MITM_CA_DIR"
echo "Generating session CA certificate..."
mitmdump --set confdir="$MITM_CA_DIR" --listen-port 0 </dev/null &>/dev/null &
TMPPID=$!; sleep 2; kill $TMPPID 2>/dev/null || true

echo "Trusting session CA (sudo — removed automatically on exit)..."
sudo security add-trusted-cert -d -r trustRoot -k "/Library/Keychains/System.keychain" "$MITM_CA_DIR/mitmproxy-ca-cert.pem"
CA_FINGERPRINT=$(openssl x509 -in "$MITM_CA_DIR/mitmproxy-ca-cert.pem" -noout -fingerprint -sha1 2>/dev/null | sed "s/.*=//;s/://g")

# Keep the sudo timestamp warm for the life of the session so teardown's CA
# removal never blocks on a password prompt (a prompt would eat Ctrl+C). Exits
# itself if creds are ever revoked. Killed in cleanup().
( while true; do sudo -n true 2>/dev/null || exit 0; sleep 30; done ) &
SUDO_KEEPALIVE_PID=$!

lsof -ti:$PROXY_PORT 2>/dev/null | xargs kill 2>/dev/null || true
osascript -e 'tell app "Claude" to quit' 2>/dev/null || true
sleep 1

# Patch Claude Desktop's MCP config to wrap each server with the Synkro stdio
# proxy (restored on exit). Capture URL + token come from the inherited env.
if [ -f "$CLAUDE_CONFIG" ]; then
  cp "$CLAUDE_CONFIG" "$CLAUDE_CONFIG_BACKUP"
  if python3 "$MCP_PATCH" "$CLAUDE_CONFIG" "$MCP_PROXY" "$SYNKRO_CAPTURE_URL" "$SYNKRO_TAP_TOKEN" 2>/dev/null; then
    MCP_PATCHED=true
    echo "MCP servers wrapped with Synkro proxy"
  else
    echo "No MCP servers configured — skipping MCP proxy"
    cp "$CLAUDE_CONFIG_BACKUP" "$CLAUDE_CONFIG" 2>/dev/null || true
  fi
fi

echo "Starting proxy on :$PROXY_PORT..."
# stdin from /dev/null so mitmdump can't grab the TTY in raw mode — otherwise it
# captures keypresses (incl. Ctrl+C) instead of letting them become a signal.
mitmdump --set confdir="$MITM_CA_DIR" --listen-port $PROXY_PORT --mode regular -s "$TAP_SCRIPT" </dev/null &>"$(dirname "$TAP_SCRIPT")/mitm.log" &
MITM_PID=$!
sleep 2

CLAUDE_BIN=""
for p in "/Applications/Claude.app/Contents/MacOS/Claude" "/Volumes/Claude/Claude.app/Contents/MacOS/Claude"; do
  [ -f "$p" ] && CLAUDE_BIN="$p" && break
done
if [ -z "$CLAUDE_BIN" ]; then
  CLAUDE_BIN="$(mdfind 'kMDItemFSName == "Claude.app"' 2>/dev/null | head -1)/Contents/MacOS/Claude"
fi
if [ ! -f "$CLAUDE_BIN" ]; then echo "Claude Desktop not found."; exit 1; fi

echo "Launching Claude Desktop..."
"$CLAUDE_BIN" --proxy-server="http://localhost:$PROXY_PORT" &>/dev/null &

echo ""
echo "  ✓ Claude Desktop is being monitored by Synkro (local)."
echo "    Your conversations will appear in the dashboard Events page."
echo "    Press Ctrl+C to stop (this removes the session CA cleanly)."
echo ""
wait $MITM_PID
`;
}

/**
 * Run a Claude Desktop observability session. Blocks (foreground) until the user
 * presses Ctrl+C, then the bash trap removes the session CA + stops the proxy.
 */
export async function runClaudeDesktopTap(opts: { backfill?: boolean } = {}): Promise<void> {
  if (process.platform !== 'darwin') {
    console.log('  Claude Desktop observability is macOS-only — skipped.');
    return;
  }
  let token = '';
  try { token = readFileSync(JWT_PATH, 'utf-8').trim(); } catch {}
  if (!token) {
    console.log('  Could not read local auth token (~/.synkro/.mcp-jwt). Run `synkro install` first.');
    return;
  }
  if (opts.backfill) {
    console.log('  History backfill enabled — importing past conversations once this session.');
  }

  // Register the synkro-guardrails MCP server in Claude Desktop so the user can
  // manage Synkro standards (rules/tasks/findings) from Desktop too — same stdio
  // proxy → local synkro-server (:18931) that Claude Code's local mode uses.
  try {
    const mcp = installClaudeDesktopMcpConfig();
    if (mcp.ok) {
      console.log(`  ✓ Synkro standards MCP registered in Claude Desktop`);
      console.log(`    (${mcp.path}) — restart Claude Desktop to load the synkro-guardrails tools.`);
    } else {
      console.log(`  ⚠ Claude Desktop MCP not registered: ${mcp.reason}`);
    }
  } catch (err) {
    console.log(`  ⚠ Could not register Claude Desktop MCP: ${(err as Error).message}`);
  }

  // Stage the tap's scripts under ~/.synkro (a Synkro-managed dir) rather than
  // /tmp — keeps all session state in one predictable, allowlisted location.
  const cdRoot = join(homedir(), '.synkro', 'cd-sessions');
  mkdirSync(cdRoot, { recursive: true });
  const sessionDir = mkdtempSync(join(cdRoot, 'synkro-cd-'));
  writeFileSync(join(sessionDir, 'tap.py'), ADDON_PY, 'utf-8');
  // MCP stdio proxy + config patcher (run by the bash to wrap configured MCP servers).
  writeFileSync(join(sessionDir, 'mcp_proxy.py'), MCP_PROXY_PY, { mode: 0o755 });
  writeFileSync(join(sessionDir, 'mcp_patch.py'), MCP_PATCH_PY, 'utf-8');
  const runnerPath = join(sessionDir, 'run.sh');
  writeFileSync(runnerPath, buildRunner(sessionDir), { mode: 0o755 });

  await new Promise<void>((resolve) => {
    // Inherit stdio so the user sees output, the sudo prompt works, and Ctrl+C
    // reaches bash's trap for a clean teardown. Token + URL are passed via env —
    // mitmdump (and thus the addon) inherits them; they never touch the source.
    const child = spawn('bash', [runnerPath], {
      stdio: 'inherit',
      env: { ...process.env, SYNKRO_CAPTURE_URL: CAPTURE_URL, SYNKRO_SCAN_URL: SCAN_URL, SYNKRO_SCAN_TURN_URL: SCAN_TURN_URL, SYNKRO_DLP_POLICY_URL: DLP_POLICY_URL, SYNKRO_TURN_VERDICTS_URL: TURN_VERDICTS_URL, SYNKRO_TURN_VERDICT_URL: TURN_VERDICT_URL, SYNKRO_MCP_EVENT_URL: MCP_EVENT_URL, SYNKRO_TAP_TOKEN: token, SYNKRO_TAP_TOKEN_FILE: JWT_PATH, SYNKRO_CD_BACKFILL: opts.backfill ? '1' : '' },
    });
    const forward = (sig: NodeJS.Signals) => { try { child.kill(sig); } catch {} };
    process.on('SIGINT', forward);
    process.on('SIGTERM', forward);
    child.on('exit', () => {
      process.off('SIGINT', forward);
      process.off('SIGTERM', forward);
      resolve();
    });
  });
}
